import React, { SetStateAction, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import {
	SampleWithInstanceAndSpecSortColumn,
	SortOrder,
} from "node-library/dist/paginations";
import {
	FrontendAdminViewSampleWithInstanceAndSpec,
	FrontendCompanyWithAdditionalInfomation,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import {
	FrontendContentPage,
	FrontendInstanceTestStatusFilter,
	Severity,
} from "node-library/dist/constants";

import { Pagination } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import { BlackButton } from "../../../common-components/button";
import {
	CustomDropdown,
	CustomRadioGroup,
	CustomSearchBar,
	RowDropdown,
} from "../../../common-components/inputs";
import { SortArrowIcon } from "../../../common-components/svgIcons";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import {
	getInstanceStatusFilterTranslationText,
	getSampleWithInstanceAndSpecSortString,
} from "../../../helpers/sampleInstanceHelper";
import { sendGETRequest } from "../../../requestHelper/sendRequest";
import AdminViewInstancesContent from "./adminViewInstancesContent";
import InstancePageSkeletonUI from "./instancePageSkeleton";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	user: FrontendUserInfo | undefined;

	navigateToOrderDetailsPage: (
		orderId: string,
		fromPage: FrontendContentPage
	) => void;

	navigateToInstanceDetailsPage: (
		instanceId: string,
		fromPage: FrontendContentPage
	) => void;

	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function AdminViewInstancePage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const company = props.company;

	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [adminSampleInstances, setAdminSampleInstances] = useState<
		Array<FrontendAdminViewSampleWithInstanceAndSpec>
	>([]);

	const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
		company.companyTestCategories && company.companyTestCategories.length > 0
			? company.companyTestCategories[0].testCategoryId
			: ""
	);
	const [loading, setLoading] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	//paginations
	const [totalPageCount, setTotalPageCount] = useState<number>(0);
	const [sortBy, setSortBy] = useState<SampleWithInstanceAndSpecSortColumn>(
		SampleWithInstanceAndSpecSortColumn.EXPECTED_COMPLETION_TIME
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
	const [currentPagination, setCurrentPagination] = useState<number>(1);

	const [statusFilterOption, setStatusFilterOption] = useState<
		FrontendInstanceTestStatusFilter | ""
	>(FrontendInstanceTestStatusFilter.IN_PROGRESS);

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}

	async function getInstances(searchText: string) {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/sample-instances/admin-get-sample-instances",
				{
					searchText,
					sortBy,
					sortOrder,
					pageNumber: currentPagination,
					statusFilterOption,
					testCategoryId: selectedCategoryId,
				}
			);

			const frontendAdminInstances: Array<FrontendAdminViewSampleWithInstanceAndSpec> =
				response?.data?.frontendAdminInstances;
			const pageCount: number = response?.data?.totalPages;
			setAdminSampleInstances(frontendAdminInstances);
			setTotalPageCount(pageCount);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	const categoryDropdownOptions: Array<{
		title: string;
		value: string;
		renderValue?: string;
	}> = company.companyTestCategories.map((category) => ({
		title: category.testCategoryName,
		value: category.testCategoryId,
	}));

	const selectCategoryDropdown: React.ReactNode = (
		<CustomDropdown
			id="category-dropdown"
			label={translation("TestCategory")}
			options={categoryDropdownOptions}
			value={selectedCategoryId}
			setValue={(value) => {
				setSelectedCategoryId(value);
				setAdminSampleInstances([]);
			}}
			displayEmpty
		/>
	);

	const statusSelectionOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
		children?: any;
	}> = Object.values(FrontendInstanceTestStatusFilter).map(
		(statusFilter: FrontendInstanceTestStatusFilter) => {
			const name: string = getInstanceStatusFilterTranslationText(
				statusFilter,
				translation
			);
			return {
				key: statusFilter,
				name: name,
				description: "",
				value: statusFilter,
				children: (
					<div
						className={`center-text status-selection-radio-option typography-body-02 ${
							statusFilterOption === statusFilter
								? "status-selection-radio-option-selected"
								: ""
						}`}
					>
						{name}
					</div>
				),
			};
		}
	);

	const statusSelection: React.ReactNode = (
		<div
			style={{
				maxWidth: "700px",
				overflowX: "auto",
			}}
		>
			<CustomRadioGroup
				options={statusSelectionOptions}
				selectedOption={statusFilterOption}
				onChange={(e) => {
					setStatusFilterOption(e.target.value);
				}}
				className="status-selection-radio-group"
			/>
		</div>
	);

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="order-sort-order-dropdown"
				className="sort-order-dropdown"
				label={"a"}
				labelClassName="hide"
				options={Object.values(SampleWithInstanceAndSpecSortColumn).map(
					(value) => {
						return {
							value: value,
							title: getSampleWithInstanceAndSpecSortString(value, translation),
						};
					}
				)}
				value={sortBy}
				setValue={setSortBy}
				noBorder
			></RowDropdown>
		</div>
	);

	useEffect(() => {
		getInstances(searchText);
	}, [
		selectedCategoryId,
		currentPagination,
		sortBy,
		sortOrder,
		statusFilterOption,
	]);

	const viewOrderTitle: React.ReactNode = (
		<div className="view-orders-page-header">
			<div className="typography-heading-01">
				{translation("SampleInstances")}
			</div>
			<CustomSearchBar
				id="view-order-page-searchbar"
				searchText={searchText}
				setSearchText={setSearchText}
				placeholder={translation("SearchByOrderNumberOrClientCompanyName")}
				searchButtonText={translation("Search")}
				onSearch={() => {
					if (currentPagination !== 1) {
						setCurrentPageIndex(1);
					} else {
						getInstances(searchText);
					}
				}}
			></CustomSearchBar>
		</div>
	);

	const generateBatchButton: React.ReactNode = (
		<BlackButton
			id={"generate-batch-button"}
			size={"flex"}
			buttonClassName="button-with-left-icon"
			onClick={() => {
				console.log("You clicked me!!!");
			}}
		>
			<AddIcon />
			<span
			// className="new-order-collapsible-text"
			>
				{translation("GenerateBatch")}
			</span>
		</BlackButton>
	);

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						marginTop: "60px",
						minHeight: "90vh",
					}}
				>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						{viewOrderTitle}
						{selectCategoryDropdown}

						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: "10px",
							}}
						>
							{statusSelection}
							{sortOptionSelection}
						</div>
						<div>{generateBatchButton}</div>

						{loading ? (
							<InstancePageSkeletonUI />
						) : (
							<AdminViewInstancesContent
								instances={adminSampleInstances}
								user={props.user}
								// sortBy={sortBy}
								// setSortBy={setSortBy}
								// sortOrder={sortOrder}
								// setSortOrder={setSortOrder}
								navigateToOrderDetailsPage={props.navigateToOrderDetailsPage}
								navigateToInstanceDetailsPage={
									props.navigateToInstanceDetailsPage
								}
							/>
						)}
					</div>
					{!loading && adminSampleInstances.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Pagination
								count={totalPageCount}
								page={currentPagination}
								onChange={(e, value) => {
									setCurrentPagination(value);
								}}
								sx={{
									backgroundColor: "white",
									borderRadius: "30px",
									padding: "6px 16px",

									"& .MuiPaginationItem-root": {
										backgroundColor: "white",
										color: "black",
										border: "1px solid transparent", // Hide outline when not selected
										"&:hover": {
											backgroundColor: "#f5f5f5", // Change background on hover
										},
										borderRadius: "50%",
									},
									"& .Mui-selected": {
										backgroundColor: SecondaryColor.SECONDARY_50, // Change background for selected item
										border: `1px solid ${NeutralColor.NEUTRAL_GREY_950}`, // Change text color for selected item
									},
								}}
								variant="outlined"
								shape="rounded"
							/>
						</div>
					)}
				</div>
			);
			break;
	}

	return <div style={{ margin: "0 auto" }}>{currentComponent}</div>;
}
